import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectPayments, ConnectBalances, ConnectPaymentDetails, ConnectDocuments,  ConnectNotificationBanner, ConnectComponentsProvider } from "@stripe/react-connect-js";
import { server } from "../../server";
import DashboardSideBar from '../../components/Shop/Layout/DashboardSideBar';
import DashboardHeader from '../Shop/Layout/DashboardHeader';
import ShopManagementPageHero from '../Shop/Layout/ShopManagementPageHero';
import { useDispatch } from "react-redux";
import { loadSeller } from "../../redux/actions/user";
import BentoEmptyState from '../Layout/BentoEmptyState';
import { getStoredCurrentProfile, isShopProfile } from '../../utils/shopProfile';

export default function StripePaymentDashboard() {
    const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
    const [error, setError] = useState('');
    const currentProfile = getStoredCurrentProfile();
    const [visible, setVisible] = React.useState(false);
    const onOpen = () => {
        setVisible(true);
    };
    const onClose = () => {
        setVisible(false);
    };
    const dispatch = useDispatch();

    useEffect(() => {
            if (!isShopProfile(currentProfile)) {
                return;
            }

            dispatch(loadSeller(currentProfile));
        }, [currentProfile, dispatch]);

    useEffect(() => {
                if (!isShopProfile(currentProfile)) {
                        setError('');
                        return;
                }

        const fetchClientSecret = async () => {
            try {
                // Fetch the AccountSession client secret using Axios
                const { data } = await axios.post(`${server}/shop/account-session`, {}, { withCredentials: true, headers: {
                    'Current-Profile': currentProfile, 
                  } });

                if (data.client_secret) {
                    const connectInstance = await loadConnectAndInitialize({
                        publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
                        clientSecret: data.client_secret,
                    });
                    setStripeConnectInstance(connectInstance);
                    setError('');
                } else {
                    throw new Error('Client secret not available');
                }
            } catch (error) {
                console.error('An error occurred: ', error);
                setError(error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred');
                setStripeConnectInstance(null);
            }
        };

        fetchClientSecret();
    }, [currentProfile]);

    if (!isShopProfile(currentProfile)) {
        return (
            <div className="shop-management-page">
                <DashboardHeader />
                <div className="shop-management-layout">
                    <div className="shop-management-sidebar-slot">
                        <DashboardSideBar active={14} />
                    </div>
                    <main className="shop-management-main-panel">
                        <ShopManagementPageHero
                            eyebrow="Payments"
                            title="Payments Dashboard"
                            copy="Review Stripe balances, payouts, transactions, and account documents for the active shop."
                        />
                        <section className="stripe-payment-dashboard-shell">
                            <BentoEmptyState
                                title="No shop profile selected"
                                description="Select a shop profile before loading the payments dashboard."
                                actionLabel="Go To Shop Login"
                                actionTo="/shop-login"
                                tiles={[
                                    {
                                        title: 'Payments',
                                        description: 'Stripe balance and payout data is attached to the active seller profile.',
                                    },
                                    {
                                        title: 'Documents',
                                        description: 'Verification and compliance documents load after the seller context is restored.',
                                    },
                                    {
                                        title: 'Transactions',
                                        description: 'Payment history and account activity will appear here for the selected shop.',
                                    },
                                    {
                                        title: 'Account',
                                        description: 'Stripe account session setup depends on the current shop profile.',
                                    },
                                ]}
                            />
                        </section>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="shop-management-page">
        <DashboardHeader />
        <div className="shop-management-layout">
        <div className="shop-management-sidebar-slot">
          <DashboardSideBar active={14} />
        </div>
        <main className="shop-management-main-panel">
            <ShopManagementPageHero
                eyebrow="Payments"
                title="Payments Dashboard"
                copy="Review Stripe balances, payouts, transactions, and account documents for the active shop."
            />
            <section className="stripe-payment-dashboard-shell">
            {error && (
                <div id="error" className="stripe-payment-dashboard-error">
                    {error}
                </div>
            )}
            {stripeConnectInstance ? (
                <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                    <ConnectNotificationBanner />
                    <ConnectBalances />
                    <ConnectPayments />
                    {visible && <ConnectPaymentDetails
          payment="{{PAYMENT_OR_PAYMENT_INTENT_ID}}"
          onClose={onClose}
        />}
                    <ConnectDocuments />
                </ConnectComponentsProvider>
            ) : !error ? (
                <p>Loading...</p>
            ) : null}
            </section>
        </main>
        </div>
        </div>
    );
}
